import Icon from "@/components/Icon";
import MultiSelect from "@/components/MultiSelect";
import SearchInput from "@/components/SearchInput";
import Table, {
  TableControls,
  TableFooter,
  buildActionColumn,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildTwoLineColumn
} from "@/components/Table";
import Tag from "@/components/Tag";
import Tooltip from "@/components/Tooltip";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePageTitle from "@/hooks/usePageTitle";
import usePagination from "@/hooks/usePagination";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import {
  compareIsoDatesNullsLast,
  getStatusData,
  isPersonal,
  isW8,
  taxClassificationDisplay
} from "@/pages/people/[pgaId]/billing/payout-profiles/utils";
import BankInstitutionCell from "@/pages/people/[pgaId]/billing/payout-profiles/_components/BankInstitutionCell";
import TokenizedAccountNumberHint from "@/pages/people/[pgaId]/billing/payout-profiles/_components/TokenizedAccountNumberHint";
import { getTime } from "@/lib/dateHelpers";
import { Path, useNavigate } from "@/router";
import { DateTime } from "luxon";
import { useQuery } from "@apollo/client";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { isEmpty, reject } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import TabLayout from "../_TabLayout";
import type { PayoutProfileNode } from "./queries";
import { ALL_PAYOUT_PROFILES_QUERY } from "./queries";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Incomplete" },
  { value: "processing", label: "Processing" },
  { value: "waiting_for_approval", label: "Waiting for Approval" },
  { value: "waiting_for_bank_info", label: "Waiting for Bank Info" },
  { value: "failed_to_sync", label: "Profile Errors" },
  { value: "deactivated", label: "Inactive" },
  { value: "deactivating", label: "Deactivating" },
  { value: "rejected", label: "Rejected" }
];

export default function PayoutProfilesPage() {
  usePageTitle("/billing/payout-profiles", "Billing | Payout Profiles");
  const { trackEvent } = useAmplitude();
  const hasTrackedView = useRef(false);
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();

  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>(params.statuses || []);
  const [variables, setVariables] = useState<{
    query?: string;
    statuses?: string[];
  }>({ ...params });
  const [sorting, setSorting] = useState<SortingState>([{ id: "profile", desc: false }]);
  const { isCompactLayout } = useIsCompactLayout();

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "query",
    debounceMs: 300
  });

  const { data, loading } = useQuery(ALL_PAYOUT_PROFILES_QUERY, {
    variables: {
      ...variables,
      query: debouncedSearch || undefined,
      statuses: variables.statuses?.length ? variables.statuses : undefined,
      ...paginationVariables
    }
  });

  useEffect(() => {
    if (hasTrackedView.current || loading || !data?.payoutProfiles) {
      return;
    }

    hasTrackedView.current = true;
    trackEvent(
      BILLING_ANALYTICS_EVENTS.payoutProfilesViewed,
      getBillingAnalyticsData({
        source: "payout_profiles_tab",
        resultCount: data.payoutProfiles.nodes?.length
      })
    );
  }, [data, loading, trackEvent]);

  const tableData = data?.payoutProfiles?.nodes || [];

  const filterState = {
    status: selectedStatusValues.length > 0
  };
  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = () => {
    setVariables({});
    clearSearch();
    setSelectedStatusValues([]);
    resetPagination();
    deleteSearchParams(["statuses[]", "query"]);
  };

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      ...(debouncedSearch && { query: debouncedSearch }),
      ...(selectedStatusValues.length > 0 && { statuses: selectedStatusValues })
    }));
  }, [debouncedSearch, selectedStatusValues]);

  const getPgaId = (row: PayoutProfileNode) => row.person?.pgaId;

  const columns: ColumnDef<PayoutProfileNode>[] = [
    ...buildAvatarDetailsColumn<PayoutProfileNode>({
      isCompactLayout,
      getTitle: (row) =>
        row.person ? `${row.person.firstName ?? ""} ${row.person.lastName ?? ""}`.trim() || row.name : row.name,
      getSubtitle: (row) => row.person?.mainProgramType?.name || "",
      getPicture: (row) => row.person?.profilePhoto ?? undefined,
      header: "Name",
      id: "fullName",
      size: 240,
      minSize: 200
    }),
    buildCopyTextColumn<PayoutProfileNode>({
      accessorKey: "pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => getPgaId(row) ?? "",
      enableSorting: false
    }),
    buildCopyTextColumn<PayoutProfileNode>({
      accessorKey: "email",
      header: "Email",
      copyFieldLabel: "Email",
      getText: (row) => row.person?.email ?? "",
      enableSorting: false
    }),
    buildTwoLineColumn<PayoutProfileNode>({
      accessorKey: "taxFormType",
      header: "Profile",
      getPrimaryText: (row) => (isPersonal(row) ? "Personal" : "Business"),
      getSecondaryText: (row) => (isW8(row) ? "Foreign" : taxClassificationDisplay(row.taxClassification || "--")),
      size: 200,
      minSize: 150,
      left: (row) => {
        if (!row.defaultPayoutProfile) return null;
        return (
          <Tooltip
            variant="plain"
            supportingText="Default Payout Profile"
            color="black"
            menuCorner="end-start"
            anchorCorner="start-start"
          >
            <Icon name="check" size={24} />
          </Tooltip>
        );
      }
    }),
    {
      id: "recipient",
      header: "Recipient",
      enableSorting: true,
      accessorFn: (row) => {
        const businessName = row.businessName;
        const name = row.name;
        return businessName && businessName !== "--" ? businessName : name;
      },
      cell: ({ row }) => {
        const businessName = row.original.businessName;
        const name = row.original.name;
        return businessName && businessName !== "--" ? businessName : name;
      }
    },
    {
      header: "Institution",
      accessorKey: "bankName",
      enableSorting: true,
      cell: ({ row }) => (
        <BankInstitutionCell
          bankName={row.original.bankName}
          bankConnectionProvider={row.original.bankConnectionProvider}
        />
      )
    },
    {
      header: "Account Number",
      accessorKey: "bankAccountMaskedNumber",
      enableSorting: true,
      cell: ({ row }) => (
        <TokenizedAccountNumberHint
          maskedNumber={row.original.bankAccountMaskedNumber}
          identifierType={row.original.stripeFcAccountNumberIdentifierType}
        />
      )
    },
    {
      id: "stripeFcExpectedExpiryDate",
      header: "Bank Expiry",
      accessorKey: "stripeFcExpectedExpiryDate",
      enableSorting: true,
      sortingFn: (rowA, rowB) =>
        compareIsoDatesNullsLast(
          rowA.original.stripeFcExpectedExpiryDate,
          rowB.original.stripeFcExpectedExpiryDate,
          sorting.some((s) => s.id === "stripeFcExpectedExpiryDate" && s.desc)
        ),
      cell: ({ row }) => row.original.stripeFcExpectedExpiryDate || <span className="opacity-20">--</span>
    },
    {
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      meta: { headerClassName: "hide-header-in-column" },
      cell: ({ row }) => {
        const statusData = getStatusData(row.original);
        return (
          <div className="flex flex-col gap-1 items-start">
            <Tag tagType={statusData.tagType} size="small">
              {statusData.label}
            </Tag>
            {row.original.bankReauthorizationRequired && (
              <Tag tagType="error" size="small">
                Bank Reauth Needed
              </Tag>
            )}
          </div>
        );
      }
    },
    {
      header: "Last Updated",
      accessorKey: "updatedAt",
      enableSorting: true,
      cell: ({ row }) => getTime(row.original.updatedAt, DateTime.DATE_MED)
    },
    buildActionColumn<PayoutProfileNode>({
      getActions: (row) => {
        const pgaId = getPgaId(row);
        return [
          {
            text: "View Profile",
            onClick: () =>
              pgaId &&
              navigate(
                `/people/${pgaId}/billing/payout-profiles/${row.id}/view?return_to=/billing/payout-profiles&${searchParamsUrl}` as Path
              )
          }
        ];
      },
      onViewClick: (row) => {
        const pgaId = getPgaId(row);
        if (pgaId) {
          navigate(
            `/people/${pgaId}/billing/payout-profiles/${row.id}/view?return_to=/billing/payout-profiles&${searchParamsUrl}` as Path
          );
        }
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu
    })
  ];

  const handleRowClick = (row: PayoutProfileNode) => {
    const pgaId = getPgaId(row);
    if (pgaId) {
      navigate(
        `/people/${pgaId}/billing/payout-profiles/${row.id}/view?return_to=/billing/payout-profiles&${searchParamsUrl}` as Path
      );
    }
  };

  const form = useForm();
  const { control } = form;

  return (
    <TabLayout>
      <div className="flex w-full">
        <div className="w-full">
          <div className="w-full">
            <div className="pt-6" />
            <Table
              data-testid="payout-profiles-table"
              data={tableData}
              columns={columns}
              loading={loading}
              sortingState={sorting}
              onSortingChange={setSorting}
              onClickRow={(row) => handleRowClick(row.original)}
              hasActiveFilters
              renderControls={() => (
                <FormProvider {...form}>
                  <TableControls
                    pagination={createHeaderPagination(data?.payoutProfiles)}
                    useExpandedFilters={true}
                    expandedFiltersState={filterState}
                    onClearAllFilters={handleClearAllFilters}
                    activeFilters={activeFilterCount}
                  >
                    <SearchInput
                      placeholder="Search Payout Profiles"
                      query={searchValue}
                      onClear={() => {
                        clearSearch();
                        resetPagination();
                      }}
                      onChange={(e) => {
                        setSearchValue(e);
                        resetPagination();
                      }}
                    />
                    <div data-name="status">
                      <Controller
                        name="status"
                        control={control}
                        render={() => (
                          <MultiSelect
                            label="Status"
                            selectedValues={selectedStatusValues}
                            options={STATUS_OPTIONS}
                            onSelect={(value, checked) => {
                              const status = checked
                                ? [...selectedStatusValues, value]
                                : selectedStatusValues.filter((v) => v !== value);

                              setSelectedStatusValues(reject(status, isEmpty));
                              setVariables((prev) => ({
                                ...prev,
                                statuses: reject(status, isEmpty)
                              }));
                              resetPagination();
                              addSearchParam("statuses[]", reject(status, isEmpty).join(","));
                            }}
                            onClear={() => {
                              setSelectedStatusValues([]);
                              setVariables((prev) => ({ ...prev, statuses: [] }));
                              deleteSearchParams("statuses[]");
                            }}
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                  </TableControls>
                </FormProvider>
              )}
              renderFooter={() => <TableFooter pagination={createFooterPagination(data?.payoutProfiles)} />}
            />
          </div>
        </div>
      </div>

      <Menu />
    </TabLayout>
  );
}
